import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Download, FileText, Image, File, ChevronLeft, ChevronRight, ShieldCheck, Printer } from 'lucide-react';
import { apiClient } from '../lib/api';
import StudentFileAuditModal from './StudentFileAuditModal';

interface StudentFile {
  file_id: number;
  original_name: string;
  mime_type: string;
  sort_order?: number | null;
  file_type: 'private' | 'official';
  updated_at?: string | null;
}

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: number;
  studentUnid: string;
  fileName: string;
  mimeType: string;
  allFiles?: StudentFile[];
}

export default function DocumentViewer({
  isOpen,
  onClose,
  fileId,
  studentUnid,
  fileName,
  mimeType,
  allFiles = []
}: DocumentViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFileId, setCurrentFileId] = useState(fileId);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  // Dateien in der Reihenfolge wie sie übergeben wurden (keine zusätzliche Sortierung)
  // Die Sortierung wird bereits in StudentFilesManager gemacht
  const orderedFiles = useMemo(() => {
    return allFiles;
  }, [allFiles]);

  // Finde aktuellen Index basierend auf file_id
  const currentIndex = useMemo(() => {
    const idx = orderedFiles.findIndex(f => f.file_id === currentFileId);
    console.log('[DocumentViewer] currentIndex:', idx, 'currentFileId:', currentFileId, 'total files:', orderedFiles.length);
    return idx;
  }, [orderedFiles, currentFileId]);

  // Aktuelle Datei
  const currentFile = useMemo(() => {
    const file = orderedFiles.find(f => f.file_id === currentFileId);
    if (file) {
      console.log('[DocumentViewer] currentFile:', file.original_name, 'file_id:', file.file_id);
    }
    return file || (orderedFiles.length > 0 ? orderedFiles[0] : null);
  }, [orderedFiles, currentFileId]);

  // Navigation Funktionen
  const navigateToFile = useCallback((newFileId: number) => {
    console.log('[DocumentViewer] navigateToFile:', newFileId);
    setCurrentFileId(newFileId);
    setLoading(true);
    setError(null);
  }, []);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      const prevFile = orderedFiles[currentIndex - 1];
      console.log('[DocumentViewer] goToPrevious to:', prevFile.original_name, 'file_id:', prevFile.file_id);
      navigateToFile(prevFile.file_id);
    }
  }, [currentIndex, orderedFiles, navigateToFile]);

  const goToNext = useCallback(() => {
    if (currentIndex < orderedFiles.length - 1) {
      const nextFile = orderedFiles[currentIndex + 1];
      console.log('[DocumentViewer] goToNext to:', nextFile.original_name, 'file_id:', nextFile.file_id);
      navigateToFile(nextFile.file_id);
    }
  }, [currentIndex, orderedFiles, navigateToFile]);

  // Sync mit prop wenn sich fileId ändert
  useEffect(() => {
    if (fileId !== currentFileId) {
      console.log('[DocumentViewer] fileId prop changed from', currentFileId, 'to', fileId);
      setCurrentFileId(fileId);
      setLoading(true);
      setError(null);
    }
  }, [fileId]);

  // Keyboard Navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToPrevious, goToNext, onClose]);

  if (!isOpen) return null;

  // URL Generator für API-Aufrufe (nutzt zentrale API-Basis-URL)
  const getApiUrl = (path: string): string => {
    const baseUrl = apiClient.getBaseUrl(); // z.B. "/api" (dev) oder "https://domain/api" (prod)
    const cleanPath = path.startsWith('/') ? path : `/${path}`;

    // Verhindere doppelte "/api" Präfixe
    if (baseUrl.endsWith('/api') && cleanPath.startsWith('/api/')) {
      return `${baseUrl}${cleanPath.slice(4)}`;
    }

    return `${baseUrl}${cleanPath}`;
  };

  // Datei-Informationen für Anzeige
  const displayFileName = currentFile?.original_name || fileName;
  const displayMimeType = currentFile?.mime_type || mimeType;

  // Dateityp-Flags für aktuelles Dokument
  const currentIsPDF = displayMimeType === 'application/pdf';
  const currentIsImage = displayMimeType?.startsWith('image/') || false;
  const currentIsDocx = displayMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                        displayMimeType === 'application/msword';
  const currentIsText = displayMimeType?.startsWith('text/') || 
                        displayMimeType === 'application/json' ||
                        displayMimeType === 'application/xml';

  // URLs für aktuelles Dokument
  const viewUrl = currentIsDocx 
    ? getApiUrl(`/api/student-files/${studentUnid}/convert-docx/${currentFileId}`)
    : getApiUrl(`/api/student-files/${studentUnid}/view/${currentFileId}`);
  const downloadUrl = getApiUrl(`/api/student-files/${studentUnid}/download/${currentFileId}`);

  const handleDownload = () => {
    window.open(downloadUrl, '_blank');
  };

  const handlePrint = () => {
    const container = contentContainerRef.current;
    if (!container) return;
    const iframe = container.querySelector('iframe');
    if (iframe && (iframe as HTMLIFrameElement).contentWindow) {
      (iframe as HTMLIFrameElement).contentWindow!.print();
    } else {
      const img = container.querySelector('img');
      if (img) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`<html><body style="margin:0;display:flex;justify-content:center;"><img src="${img.src}" style="max-width:100%;height:auto;" /></body></html>`);
          printWindow.document.close();
          printWindow.onload = () => { printWindow.print(); printWindow.close(); };
        }
      } else {
        window.print();
      }
    }
  };

  // Helper-Funktionen für Dateityp-Erkennung
  const getFileTypeInfo = (mimeType: string | undefined) => {
    const mime = mimeType || '';
    const isPDF = mime === 'application/pdf';
    const isImage = mime.startsWith('image/');
    const isDocx = mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                   mime === 'application/msword';
    const isText = mime.startsWith('text/');
    
    let icon = '📁';
    let bgColor = 'bg-gray-100 dark:bg-gray-800';
    
    if (isPDF) { icon = '📄'; bgColor = 'bg-red-50 dark:bg-red-900/20'; }
    else if (isDocx) { icon = '📝'; bgColor = 'bg-blue-50 dark:bg-blue-900/20'; }
    else if (isText) { icon = '📃'; bgColor = 'bg-yellow-50 dark:bg-yellow-900/20'; }
    else if (isImage) { icon = '🖼️'; bgColor = 'bg-green-50 dark:bg-green-900/20'; }
    
    return { isPDF, isImage, isDocx, isText, icon, bgColor };
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="flex h-full flex-col items-center justify-center p-8">
          <File className="h-16 w-16 text-red-400 mb-4" />
          <p className="mb-2 text-red-600 dark:text-red-300">Fehler beim Laden des Dokuments</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <button
            onClick={handleDownload}
            className="mt-4 btn btn-primary flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            Stattdessen herunterladen
          </button>
        </div>
      );
    }

    if (currentIsPDF) {
      return (
        <div className="w-full h-full">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Lade PDF...</p>
              </div>
            </div>
          )}
          <iframe
            key={`pdf-${currentFileId}`}
            src={viewUrl}
            className="w-full h-full border-0"
            title={displayFileName}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError('PDF konnte nicht geladen werden');
            }}
          />
        </div>
      );
    }

    if (currentIsImage) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-gray-50 p-4 dark:bg-gray-900">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Lade Bild...</p>
              </div>
            </div>
          )}
          <img
            key={`img-${currentFileId}`}
            src={viewUrl}
            alt={displayFileName}
            className="max-w-full max-h-full object-contain"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError('Bild konnte nicht geladen werden');
            }}
          />
        </div>
      );
    }

    if (currentIsDocx) {
      return (
        <div className="w-full h-full">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Konvertiere Word-Dokument...</p>
              </div>
            </div>
          )}
          <iframe
            key={`docx-${currentFileId}`}
            src={viewUrl}
            className="h-full w-full border-0 bg-white dark:bg-gray-900"
            title={displayFileName}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError('Word-Dokument konnte nicht geladen werden');
            }}
          />
        </div>
      );
    }

    if (currentIsText) {
      return (
        <div className="w-full h-full">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">Lade Textdatei...</p>
              </div>
            </div>
          )}
          <iframe
            key={`text-${currentFileId}`}
            src={viewUrl}
            className="h-full w-full border-0 bg-white dark:bg-gray-900"
            title={displayFileName}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError('Textdatei konnte nicht geladen werden');
            }}
          />
        </div>
      );
    }

    // Fallback für nicht unterstützte Dateitypen
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <p className="mb-2 text-gray-600 dark:text-gray-300">Vorschau für diesen Dateityp nicht verfügbar</p>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">Dateityp: {displayMimeType}</p>
        <button
          onClick={handleDownload}
          className="btn btn-primary flex items-center"
        >
          <Download className="h-4 w-4 mr-2" />
          Datei herunterladen
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
      <div className="flex h-full max-h-[90vh] w-full max-w-6xl flex-col rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/40">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {currentIsPDF && <FileText className="h-5 w-5 text-red-600 flex-shrink-0" />}
            {currentIsImage && <Image className="h-5 w-5 text-blue-600 flex-shrink-0" />}
            {currentIsDocx && <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />}
            {!currentIsPDF && !currentIsImage && !currentIsDocx && <File className="h-5 w-5 flex-shrink-0 text-gray-600 dark:text-gray-300" />}
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                {displayFileName}
              </h3>
              {orderedFiles.length > 1 && (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {currentIndex + 1} von {orderedFiles.length}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {/* Navigation Buttons */}
            {orderedFiles.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  disabled={currentIndex <= 0}
                  className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                  title="Vorherige Datei (←)"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={currentIndex >= orderedFiles.length - 1}
                  className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                  title="Nächste Datei (→)"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              onClick={handlePrint}
              className="btn btn-secondary btn-sm flex items-center"
              title={`Drucken: ${displayFileName}`}
            >
              <Printer className="h-4 w-4" />
            </button>
            <button
              onClick={handleDownload}
              className="btn btn-secondary btn-sm flex items-center"
              title={`Speichern: ${displayFileName}`}
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowAuditModal(true)}
              className="btn btn-secondary btn-sm flex items-center"
              title="Zugriffshistorie (DSGVO)"
            >
              <ShieldCheck className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
              title="Schließen (ESC)"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={contentContainerRef} className="relative flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900">
          {renderContent()}
        </div>

        {/* Thumbnail Navigation - Alle Dateien in Originalreihenfolge */}
        {orderedFiles.length > 1 && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/80">
            <div className="flex items-center justify-start gap-2 overflow-x-auto pb-1">
              {orderedFiles.map((file, index) => {
                const typeInfo = getFileTypeInfo(file.mime_type);
                const isActive = file.file_id === currentFileId;
                
                // Thumbnail-URL für alle unterstützten Dateitypen
                // Backend generiert Thumbnails für: Bilder, PDF, DOCX, Text, HTML
                const supportsThumbnail = typeInfo.isImage || typeInfo.isPDF || typeInfo.isDocx || typeInfo.isText;
                const thumbnailUrl = supportsThumbnail 
                  ? getApiUrl(`/api/student-files/thumbnail/${file.file_id}?v=${encodeURIComponent(file.updated_at || String(file.file_id))}`)
                  : null;
                
                return (
                  <button
                    key={`thumb-${file.file_id}`}
                    onClick={() => {
                      console.log('[DocumentViewer] Thumbnail clicked:', file.original_name, 'file_id:', file.file_id);
                      navigateToFile(file.file_id);
                    }}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg border-2 overflow-hidden transition-all ${
                      isActive
                        ? 'z-10 scale-110 border-blue-500 ring-2 ring-blue-300 shadow-lg dark:ring-blue-700/70'
                        : 'border-gray-300 hover:border-blue-400 hover:shadow-md dark:border-gray-600 dark:hover:border-blue-400'
                    }`}
                    title={`${index + 1}. ${file.original_name}`}
                  >
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={file.original_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          // Für Bilder: Fallback zum View-Endpoint
                          if (typeInfo.isImage) {
                            const fallbackUrl = getApiUrl(`/api/student-files/${studentUnid}/view/${file.file_id}`);
                            if (!target.dataset.fallbackTried) {
                              target.dataset.fallbackTried = 'true';
                              target.src = fallbackUrl;
                              return;
                            }
                          }
                          // Icon anzeigen wenn Thumbnail nicht verfügbar
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent && !parent.querySelector('.fallback-icon')) {
                            const fallback = document.createElement('div');
                            fallback.className = `fallback-icon w-full h-full flex flex-col items-center justify-center ${typeInfo.bgColor}`;
                            fallback.innerHTML = `<span class="text-lg">${typeInfo.icon}</span><span class="text-[8px] text-gray-500 mt-0.5">${index + 1}</span>`;
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <div className={`w-full h-full flex flex-col items-center justify-center ${typeInfo.bgColor}`}>
                        <span className="text-lg">{typeInfo.icon}</span>
                        <span className="text-[8px] text-gray-500 mt-0.5">{index + 1}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <StudentFileAuditModal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        fileId={currentFileId}
        fileName={displayFileName}
      />
    </div>
  );
}
